import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Pagination from "../../components/Pagination";
import Drawer from "../../components/Drawer";
import { useApi } from "../../hooks/useApi";
import { listActiveUsers, getUserRequests } from "../../api/superAdminService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { fromNow, formatDateTime, initials } from "../../utils/formatters";

const ROLE_LABEL = { admin: "Admin", user: "Employee", customer: "Client", super_admin: "Super admin" };

// Colour the HTTP verb so writes stand out from reads at a glance.
const METHOD_BADGE = { GET: "badge-neutral", POST: "badge-success", PUT: "badge-warning", PATCH: "badge-warning", DELETE: "badge-danger" };

// Platform-wide roster of every firm user ranked by last request, with a
// live/idle badge (online = a request within the last 5 minutes, per backend).
// Read-only oversight — session force-logout lives on the Active sessions page.
export default function ActiveUsersPage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [requestsFor, setRequestsFor] = useState(null); // user whose history is open

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, onlineOnly, perPage]);

  const users = useApi(
    () => {
      const params = { page, per_page: perPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (onlineOnly) params.online = 1;
      return listActiveUsers(params);
    },
    [page, perPage, debouncedSearch, onlineOnly],
  );
  const rows = paginatedRows(users.data);
  const meta = paginatedMeta(users.data);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Active users</h1>
          <div className="page-sub">
            {meta?.total ?? rows.length} user{(meta?.total ?? rows.length) === 1 ? "" : "s"} across all firms, by last activity
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={() => users.reload()} disabled={users.loading}>
            <Icon name="refresh" size={13} /> {users.loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="filter-bar">
        {[
          { id: false, label: "All" },
          { id: true, label: "Online now" },
        ].map((f) => (
          <button
            key={String(f.id)}
            className={`chip ${onlineOnly === f.id ? "active" : ""}`}
            onClick={() => setOnlineOnly(f.id)}
          >
            {f.label}
          </button>
        ))}
        <div style={{ position: "relative", flex: 1, maxWidth: 360, marginLeft: "auto" }}>
          <Icon
            name="search"
            size={13}
            style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
          />
          <input
            className="input"
            style={{ paddingLeft: 30 }}
            placeholder="Search by name, username or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {users.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={5} /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="activity"
              title={onlineOnly ? "Nobody is online" : "No users match"}
              hint={debouncedSearch ? "Try a different search." : onlineOnly ? "No user has made a request in the last 5 minutes." : ""}
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Firm</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th className="t-num">Sessions</th>
                    <th>Last request</th>
                    <th>Presence</th>
                    <th style={{ width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <div className="row gap-sm">
                          <div className="avatar avatar-sm" style={{ background: `hsl(${(u.id * 47) % 360} 60% 55%)` }}>
                            {initials(u.name)}
                          </div>
                          <div>
                            <div className="t-primary">{u.name}</div>
                            {u.username && <div className="t-muted text-xs t-mono">{u.username}</div>}
                          </div>
                        </div>
                      </td>
                      <td>{u.firm?.name || <span className="t-muted">—</span>}</td>
                      <td><span className="badge badge-neutral">{ROLE_LABEL[u.role] || u.role}</span></td>
                      <td>
                        {u.status
                          ? <span className="badge badge-success">Active</span>
                          : <span className="badge badge-neutral">Disabled</span>}
                      </td>
                      <td className="t-num">{u.active_sessions ?? 0}</td>
                      <td className="t-muted" style={{ whiteSpace: "nowrap" }}>{fromNow(u.last_request_at)}</td>
                      <td>
                        {u.is_online
                          ? <span className="badge badge-success">Online</span>
                          : <span className="badge badge-neutral">Idle</span>}
                      </td>
                      <td>
                        <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                          <button
                            className="icon-btn"
                            onClick={() => setRequestsFor(u)}
                            title="View recent requests"
                          >
                            <Icon name="clock" size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination
            meta={meta}
            page={page}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={setPerPage}
            disabled={users.loading}
          />
        </div>
      </div>

      <UserRequestsDrawer user={requestsFor} onClose={() => setRequestsFor(null)} />
    </>
  );
}

// Slide-over showing a single user's rolling request history (newest first).
// The list is capped server-side to the last N authenticated requests.
function UserRequestsDrawer({ user, onClose }) {
  const open = !!user;
  const req = useApi(
    () => (user ? getUserRequests(user.id) : Promise.resolve(null)),
    [user?.id],
  );
  const rows = req.data?.data?.requests || [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={user ? `${user.name} — recent requests` : ""}
      subtitle="Newest first. Only the most recent requests are kept per user."
    >
      {req.loading ? (
        <LoadingBlock rows={6} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="clock"
          title="No requests logged yet"
          hint="This user hasn't made an authenticated request since logging was enabled."
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Method</th>
                <th>Path</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="t-muted" style={{ whiteSpace: "nowrap" }} title={formatDateTime(r.created_at)}>
                    {fromNow(r.created_at)}
                  </td>
                  <td><span className={`badge ${METHOD_BADGE[r.method] || "badge-neutral"}`}>{r.method}</span></td>
                  <td className="t-mono text-xs">{r.path}</td>
                  <td className="t-mono text-xs">{r.ip_address || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Drawer>
  );
}
